import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaEnvelope,
  FaFacebookF,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaExternalLinkAlt,
  FaTimes,
  FaHeart,
  FaGlobe,
  FaIdCard,
  FaPhone,
  FaUserTie,
} from "react-icons/fa";

import { getSettings } from "../../../services/settingsServices";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const DEVELOPER_PROFILE = {
  name: "Laurent VICTORIN",
  role: "Développeur Web & Web Mobile · Président & Enseignant de Judo",
  email: "monappliclub@gmail.com",
  phoneDisplay: "06 80 72 77 07",
  phoneHref: "+33680727707",
  photo:
    "https://res.cloudinary.com/dj0c0zuje/image/upload/v1744184384/PXL_20241115_211013575.MP_1_h7lbn9.jpg",
  website: "https://www.monappliclub.com/",
  vkard:
    "https://profile.vkard.io/8f8e4d95-cf28-40b0-afc6-2ec677889d49/ab099714aa",
};

const DONATION_LINKS = [
  {
    label: "5 €",
    subtitle: "Don unique",
    url: "https://buy.stripe.com/cN2aFtaqL5fC1AkcMR",
  },
];

const DEFAULT_SETTINGS = {
  nom_club: "MonAppliClub Light",
  logo_club: "",
  adresse_club: "",
  tel_club: "",
  mail_club: "",
  facebook_club: "",
  instagram_club: "",
};

const CLUB_DOJOS = [
  {
    id: "royan",
    nom: "Salle Landry",
    ville: "Royan",
    adresse: "Bd de Lattre de Tassigny, 17200 Royan, France",
  },
  {
    id: "saint-palais-sur-mer",
    nom: "Dojo de Saint-Palais-sur-Mer",
    ville: "Saint-Palais-sur-Mer",
    adresse: "13 rue des Écoles, 17420 Saint-Palais-sur-Mer, France",
  },
];

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const normalizePhoneHref = (phone = "") => {
  const clean = String(phone).replace(/\s+/g, "");

  if (!clean) return "";

  return `tel:${clean}`;
};

const sectionLinks = [
  {
    label: "À propos",
    id: "about",
  },
  {
    label: "Nos disciplines",
    id: "cours",
  },
  {
    label: "Actualités",
    id: "news",
  },
  {
    label: "Albums photos",
    id: "galerie",
  },
  {
    label: "Partenaires",
    id: "partenaires",
  },
  {
    label: "Contact",
    id: "contact",
  },
];

const legalLinks = [
  {
    label: "Mentions légales",
    to: "/mentions-legales",
  },
  {
    label: "Politique de confidentialité",
    to: "/politique-confidentialite",
  },
  {
    label: "Conditions d’utilisation",
    to: "/conditions-utilisation",
  },
];

const FooterLink = ({ children, onClick, to }) => {
  const className =
    "group inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition hover:text-primary";

  if (to) {
    return (
      <Link to={to} className={className}>
        <span>{children}</span>
        <span className="h-1 w-1 rounded-full bg-primary opacity-0 transition group-hover:opacity-100" />
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={className}>
      <span>{children}</span>
      <span className="h-1 w-1 rounded-full bg-primary opacity-0 transition group-hover:opacity-100" />
    </button>
  );
};

const ContactLine = ({ icon: Icon, label, value, href }) => {
  if (!value) return null;

  const content = (
    <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 transition hover:bg-white/10">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-primary shadow-lg">
        <Icon />
      </div>

      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
          {label}
        </p>
        <p className="mt-1 break-words text-sm font-semibold leading-6 text-white">
          {value}
        </p>
      </div>
    </div>
  );

  if (!href) return content;

  return (
    <a href={href} className="block">
      {content}
    </a>
  );
};

const DeveloperPanel = ({ open, onClose }) => {
  const donation = DONATION_LINKS[0];

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center p-3 sm:p-5">
      <button
        type="button"
        aria-label="Fermer le panneau développeur"
        className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
        onClick={onClose}
      />

      <aside className="relative max-h-[calc(100dvh-1.5rem)] w-full max-w-3xl overflow-y-auto rounded-[1.7rem] border border-white/10 bg-slate-950 text-white shadow-2xl overscroll-contain sm:max-h-[calc(100dvh-2.5rem)] sm:rounded-[2rem]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.20),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.16),transparent_34%)]" />

        <div className="relative p-4 sm:p-6">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105 sm:right-4 sm:top-4"
            aria-label="Fermer"
          >
            <FaTimes className="text-sm" />
          </button>

          <div className="flex gap-4 pr-10">
            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-white shadow-xl sm:h-20 sm:w-20">
              <img
                src={DEVELOPER_PROFILE.photo}
                alt={DEVELOPER_PROFILE.name}
                className="h-full w-full object-cover"
              />
            </div>

            <div className="min-w-0">
              <p className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white/60 sm:text-xs">
                <FaUserTie className="text-primary" />
                Créateur
              </p>

              <h3 className="mt-3 font-barlow text-2xl font-bold leading-tight text-white sm:text-3xl">
                {DEVELOPER_PROFILE.name}
              </h3>

              <p className="mt-1 text-xs font-semibold leading-5 text-white/60 sm:text-sm sm:leading-6">
                {DEVELOPER_PROFILE.role}
              </p>
            </div>
          </div>

          <div className="mt-5 rounded-[1.4rem] border border-white/10 bg-white/5 p-4 backdrop-blur-xl sm:rounded-[1.7rem] sm:p-5">
            <p className="inline-flex items-center gap-2 rounded-full border border-orange-500/20 bg-orange-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-orange-300 sm:text-xs">
              <FaHeart />
              Soutenir le projet
            </p>

            <h4 className="mt-3 font-barlow text-2xl font-bold leading-tight text-white sm:text-3xl">
              Un outil développé pour aider les clubs sportifs.
            </h4>

            <p className="mt-3 text-sm leading-6 text-white/68 sm:leading-7">
              MonAppliClub Light est développé pour permettre aux associations
              sportives de disposer d’un site moderne, responsive et facile à
              administrer. Si l’outil vous est utile, vous pouvez soutenir le
              travail réalisé par un petit don.
            </p>

            <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <a
                href={`mailto:${DEVELOPER_PROFILE.email}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2.5 text-xs font-bold text-white transition hover:bg-white hover:text-slate-950 sm:text-sm"
              >
                <FaEnvelope />
                Email
              </a>

              <a
                href={`tel:${DEVELOPER_PROFILE.phoneHref}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2.5 text-xs font-bold text-white transition hover:bg-white hover:text-slate-950 sm:text-sm"
              >
                <FaPhone />
                Appeler
              </a>

              <a
                href={DEVELOPER_PROFILE.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2.5 text-xs font-bold text-white transition hover:bg-white hover:text-slate-950 sm:text-sm"
              >
                <FaGlobe />
                Site
              </a>

              <a
                href={DEVELOPER_PROFILE.vkard}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2.5 text-xs font-bold text-white transition hover:bg-white hover:text-slate-950 sm:text-sm"
              >
                <FaIdCard />
                VCard
              </a>
            </div>

            {donation && (
              <div className="mt-5 flex flex-col gap-3 rounded-[1.4rem] border border-orange-500/20 bg-orange-500/10 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-barlow text-xl font-bold text-white sm:text-2xl">
                    Faire un don de {donation.label}
                  </p>
                  <p className="mt-1 text-xs font-semibold leading-5 text-orange-200/80 sm:text-sm">
                    {donation.subtitle} pour soutenir le développement.
                  </p>
                </div>

                <a
                  href={donation.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-primary px-5 py-3 text-sm font-bold text-white shadow-[0_18px_45px_rgba(242,113,49,0.28)] transition hover:-translate-y-0.5 hover:brightness-110"
                >
                  <FaHeart />
                  Soutenir
                </a>
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default function Footer() {
  const navigate = useNavigate();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [developerPanelOpen, setDeveloperPanelOpen] = useState(false);
  const [activeDojoId, setActiveDojoId] = useState("royan");

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted || !clubSettings) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...clubSettings,
          tel_club:
            clubSettings.tel_club ||
            clubSettings.telephone_club ||
            clubSettings.phone ||
            "",
          mail_club:
            clubSettings.mail_club ||
            clubSettings.email_club ||
            clubSettings.email ||
            "",
          adresse_club:
            clubSettings.adresse_club ||
            clubSettings.address ||
            [clubSettings.adresse, clubSettings.code_postal, clubSettings.ville]
              .filter(Boolean)
              .join(" "),
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const logoUrl = getImageUrl(settings.logo_club);

  const dojoLocations = useMemo(
    () =>
      CLUB_DOJOS.map((dojo) => {
        if (dojo.id !== "royan") {
          return dojo;
        }

        return {
          ...dojo,
          adresse: settings.adresse_club || dojo.adresse,
        };
      }),
    [settings.adresse_club],
  );

  const activeDojo = useMemo(
    () =>
      dojoLocations.find((dojo) => dojo.id === activeDojoId) ||
      dojoLocations[0],
    [dojoLocations, activeDojoId],
  );

  const activeDojoQuery = useMemo(() => {
    if (!activeDojo) return "";

    return `${activeDojo.nom}, ${activeDojo.adresse}`;
  }, [activeDojo]);

  const mapUrl = useMemo(() => {
    if (!activeDojoQuery) return "";

    return `https://www.google.com/maps?q=${encodeURIComponent(
      activeDojoQuery,
    )}&output=embed`;
  }, [activeDojoQuery]);

  const externalMapUrl = useMemo(() => {
    if (!activeDojoQuery) return "";

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      activeDojoQuery,
    )}`;
  }, [activeDojoQuery]);

  const phoneHref = useMemo(
    () => normalizePhoneHref(settings.tel_club),
    [settings.tel_club],
  );

  const emailHref = useMemo(() => {
    return settings.mail_club ? `mailto:${settings.mail_club}` : "";
  }, [settings.mail_club]);

  const handleNavigateToSection = (id) => {
    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      return;
    }

    navigate("/");

    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const hasSocials = settings.facebook_club || settings.instagram_club;

  return (
    <>
      <footer className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.18),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.16),transparent_32%)]" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.07)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />

        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
          <div className="space-y-8">
            {/* Présentation du club */}
            <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur-xl sm:p-7">
              <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={scrollToTop}
                    className="group flex max-w-full items-center gap-4 text-left"
                  >
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-[1.4rem] bg-white p-2 shadow-xl transition group-hover:-translate-y-1 sm:h-20 sm:w-20 sm:rounded-[1.7rem]">
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt={`Logo ${settings.nom_club}`}
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <span className="font-barlow text-2xl font-bold text-primary">
                          MC
                        </span>
                      )}
                    </div>

                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
                        Site officiel
                      </p>

                      <h2 className="mt-1 font-barlow text-2xl font-bold leading-tight text-white sm:text-3xl lg:text-4xl">
                        {settings.nom_club || "MonAppliClub Light"}
                      </h2>
                    </div>
                  </button>

                  <p className="mt-5 max-w-3xl text-sm leading-7 text-slate-400">
                    Retrouvez les informations du club, les disciplines, les
                    actualités, les albums photos, les partenaires et les
                    modalités de contact.
                  </p>
                </div>

                {hasSocials && (
                  <div className="shrink-0">
                    <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                      Suivez-nous
                    </p>

                    <div className="flex items-center gap-3">
                      {settings.facebook_club &&
                        isExternalURL(settings.facebook_club) && (
                          <a
                            href={settings.facebook_club}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:-translate-y-0.5 hover:bg-white hover:text-blue-600"
                            aria-label="Facebook"
                          >
                            <FaFacebookF />
                          </a>
                        )}

                      {settings.instagram_club &&
                        isExternalURL(settings.instagram_club) && (
                          <a
                            href={settings.instagram_club}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:-translate-y-0.5 hover:bg-white hover:text-pink-600"
                            aria-label="Instagram"
                          >
                            <FaInstagram />
                          </a>
                        )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Carte et navigation */}
            <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start xl:grid-cols-[minmax(0,1fr)_330px]">
              {/* Dojos et carte Google Maps */}
              <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 p-3 shadow-2xl backdrop-blur-xl">
                <div className="p-2 sm:p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                        Nos lieux de pratique
                      </p>

                      <h3 className="mt-1 font-barlow text-2xl font-bold text-white">
                        Choisissez un dojo
                      </h3>
                    </div>

                    <span className="w-fit shrink-0 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-bold text-slate-300">
                      {dojoLocations.length} dojos
                    </span>
                  </div>

                  <div className="mt-5 grid gap-3 md:grid-cols-2">
                    {dojoLocations.map((dojo) => {
                      const isActive = dojo.id === activeDojo?.id;

                      return (
                        <button
                          key={dojo.id}
                          type="button"
                          onClick={() => setActiveDojoId(dojo.id)}
                          aria-pressed={isActive}
                          className={[
                            "flex min-w-0 items-start gap-4 rounded-2xl border p-4 text-left transition",
                            isActive
                              ? "border-primary bg-primary/15 shadow-[0_14px_35px_rgba(242,113,49,0.15)]"
                              : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10",
                          ].join(" ")}
                        >
                          <span
                            className={[
                              "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg",
                              isActive
                                ? "bg-primary text-white"
                                : "bg-white text-primary",
                            ].join(" ")}
                          >
                            <FaMapMarkerAlt />
                          </span>

                          <span className="min-w-0">
                            <span
                              className={[
                                "block text-xs font-bold uppercase tracking-[0.16em]",
                                isActive ? "text-primary" : "text-slate-500",
                              ].join(" ")}
                            >
                              {dojo.ville}
                            </span>

                            <span className="mt-1 block font-barlow text-lg font-bold leading-tight text-white sm:text-xl">
                              {dojo.nom}
                            </span>

                            <span className="mt-2 block text-sm leading-6 text-slate-400">
                              {dojo.adresse}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-3 overflow-hidden rounded-[1.5rem] border border-white/10 bg-slate-900">
                  <div className="flex flex-col gap-3 border-b border-white/10 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                        Localisation affichée
                      </p>

                      <p className="mt-1 font-barlow text-lg font-bold text-white">
                        {activeDojo?.nom}
                      </p>

                      <p className="mt-1 text-xs leading-5 text-slate-400">
                        {activeDojo?.adresse}
                      </p>
                    </div>

                    {externalMapUrl && (
                      <a
                        href={externalMapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-fit shrink-0 items-center justify-center gap-2 rounded-full bg-white px-4 py-2.5 text-xs font-bold text-slate-950 transition hover:bg-primary hover:text-white"
                      >
                        Voir l’itinéraire
                        <FaExternalLinkAlt className="text-[10px]" />
                      </a>
                    )}
                  </div>

                  {mapUrl ? (
                    <iframe
                      title={`Carte de localisation ${activeDojo?.nom}`}
                      src={mapUrl}
                      width="100%"
                      height="320"
                      className="block h-[260px] w-full sm:h-[320px]"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  ) : (
                    <div className="flex h-[280px] items-center justify-center px-6 text-center text-sm text-slate-400">
                      Localisation non renseignée.
                    </div>
                  )}
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <ContactLine
                    icon={FaEnvelope}
                    label="Email"
                    value={settings.mail_club}
                    href={emailHref}
                  />

                  <ContactLine
                    icon={FaPhoneAlt}
                    label="Téléphone"
                    value={settings.tel_club}
                    href={phoneHref}
                  />
                </div>
              </div>

              {/* Navigation et informations */}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
                <div className="rounded-[2rem] border border-white/10 bg-white/5 p-7 shadow-2xl backdrop-blur-xl">
                  <h3 className="font-barlow text-2xl font-bold text-white">
                    Navigation
                  </h3>

                  <nav className="mt-5 flex flex-col items-start gap-3">
                    {sectionLinks.map((link) => (
                      <FooterLink
                        key={link.id}
                        onClick={() => handleNavigateToSection(link.id)}
                      >
                        {link.label}
                      </FooterLink>
                    ))}
                  </nav>
                </div>

                <div className="rounded-[2rem] border border-white/10 bg-white/5 p-7 shadow-2xl backdrop-blur-xl">
                  <h3 className="font-barlow text-2xl font-bold text-white">
                    Informations
                  </h3>

                  <nav className="mt-5 flex flex-col items-start gap-3">
                    {legalLinks.map((link) => (
                      <FooterLink key={link.to} to={link.to}>
                        {link.label}
                      </FooterLink>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          </div>

          {/* Barre inférieure */}
          <div className="mt-10 flex flex-col gap-4 rounded-[2rem] border border-white/10 bg-black/25 px-5 py-5 text-center text-sm text-slate-400 backdrop-blur-xl md:flex-row md:items-center md:justify-between md:text-left">
            <p>
              Copyright © {new Date().getFullYear()} - Tous droits réservés -{" "}
              <span className="font-semibold text-white">
                {settings.nom_club || "MonAppliClub Light"}
              </span>
            </p>

            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <a
                href="https://www.monappliclub.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-semibold text-white transition hover:text-primary"
              >
                MonAppliClub
                <FaExternalLinkAlt className="text-[10px]" />
              </a>

              <button
                type="button"
                onClick={() => setDeveloperPanelOpen(true)}
                className="inline-flex items-center gap-2 font-semibold text-white transition hover:text-primary"
              >
                Laurent VICTORIN
                <FaHeart className="text-[11px]" />
              </button>
            </div>
          </div>
        </div>
      </footer>

      <DeveloperPanel
        open={developerPanelOpen}
        onClose={() => setDeveloperPanelOpen(false)}
      />
    </>
  );
}
